import api from '../utils/api';
import * as T from '../types';
import {QuoteResponse, Review} from "../types";

export const UserService = {
    getAll: async (): Promise<T.ApiResponse<T.User[]>> => {
        const response = await api.get<T.ApiResponse<T.User[]>>('/api/v1/users');
        return response.data;
    },
    getById: async (id: number): Promise<T.ApiResponse<T.User>> => {
        const response = await api.get<T.ApiResponse<T.User>>(`/api/v1/users/${id}`);
        return response.data;
    },
    create: async (data: T.UserDto): Promise<T.ApiResponse<T.User>> => {
        const response = await api.post<T.ApiResponse<T.User>>('/api/v1/users', data);
        return response.data;
    },
    update: async (id: number, data: T.UserDto): Promise<T.ApiResponse<T.User>> => {
        const response = await api.put<T.ApiResponse<T.User>>(`/api/v1/users/${id}`, data);
        return response.data;
    },
    delete: async (id: number): Promise<T.ApiResponse<boolean>> => {
        const response = await api.delete<T.ApiResponse<boolean>>(`/api/v1/users/${id}`);
        return response.data;
    },
};

// --- AUTH & SECURITY ---
export const changePassword = async (data: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
    const response = await api.put('/users/change-password', data);
    return response.data;
};

// --- PUBLIC & CONTENT ---
export const getQuote = async (id: string, checkIn: string, checkOut: string, guests: number, coupon?: string): Promise<QuoteResponse> => {
    const response = await api.get(`/properties/${id}/quote`, {
        params: { checkIn, checkOut, guests, coupon }
    });
    return response.data.data;
};


export const getProperties = async (params?: {
    location?: string;
    checkIn?: string;
    checkOut?: string;
    guests?: number;
}) => {
    const response = await api.get('/properties', { params });
    return response.data.data;
};

export const getReviews = async (id: string): Promise<Review[]> => {
    try {
        const response = await api.get(`/properties/${id}/reviews`);
        return response.data.data;
    } catch (error) {
        console.error("Errore recupero recensioni", error);
        return [];
    }
};

export const getProperty = async (id: string) => {
    const response = await api.get(`/properties/${id}`);
    return response.data.data;
};

export const getCalendar = async (listingId: string, startDate: string, endDate: string) => {
    try {
        const response = await api.get(`/properties/${listingId}/calendar?from=${startDate}&to=${endDate}`);
        return response.data;
    } catch (error) {
        console.error("Error fetching calendar", error);
        return [];
    }
};

// --- BLOG ---
export const getBlogPosts = async () => {
    const response = await api.get('/blog/public');
    return response.data.data;
};

export const getBlogPostBySlug = async (slug: string) => {
    const response = await api.get(`/blog/public/${slug}`);
    return response.data.data;
};

export const adminGetAllBlogPosts = async () => {
    const response = await api.get('/blog/admin');
    return response.data.data;
};

export const adminSaveBlogPost = async (post: any) => {
    if (post.id) {
        const response = await api.put(`/blog/admin/${post.id}`, post);
        return response.data;
    }
    const response = await api.post('/blog/admin', post);
    return response.data;
};

export const adminDeleteBlogPost = async (id: number) => {
    const response = await api.delete(`/blog/admin/${id}`);
    return response.data;
};

export const certifyBlogPost = async (id: number) => {
    const response = await api.post(`/blog/admin/${id}/certify`);
    return response.data;
};

export const uploadBlogImage = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/blog/admin/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
};

// --- MANUALS (ADMIN) ---
export const adminListManuals = async () => {
    const response = await api.get('/manual/admin');
    return response.data.data;
};

export const adminCreateManual = async (data: { houseName: string; address: string; badge?: string; ownerPassword: string }) => {
    const response = await api.post('/manual/admin', data);
    return response.data;
};

export const adminResetManualPassword = async (id: number, password: string) => {
    const response = await api.post(`/manual/admin/${id}/reset-password`, { password });
    return response.data;
};

export const adminRegenerateManualSlugs = async (id: number) => {
    const response = await api.post(`/manual/admin/${id}/regenerate-slugs`);
    return response.data;
};

export const adminDeleteManual = async (id: number) => {
    const response = await api.delete(`/manual/admin/${id}`);
    return response.data;
};

export const adminGetManualFull = async (id: number) => {
    const response = await api.get(`/manual/admin/${id}/full`);
    return response.data.data;
};

export const adminSaveManual = async (id: number, data: any) => {
    const response = await api.put(`/manual/admin/${id}`, data);
    return response.data;
};

export const adminUploadManualVideo = async (
    id: number,
    file: File | Blob,
    onProgress?: (pct: number) => void
) => {
    const formData = new FormData();
    formData.append('file', file, file instanceof File ? file.name : 'video.mp4');
    const response = await api.post(`/manual/admin/${id}/upload-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        }
    });
    return response.data;
};

export const adminUploadManualImage = async (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/manual/admin/${id}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
};

// --- MANUALS (PUBLIC / GUEST) ---
export const getManualForGuest = async (guestSlug: string) => {
    const response = await api.get(`/manual/public/${guestSlug}`);
    return response.data.data;
};

// --- MANUALS (OWNER) ---
export const ownerAuth = async (editSlug: string, password: string) => {
    const response = await api.post(`/manual/owner/${editSlug}/auth`, { password });
    return response.data;
};

export const ownerGetManual = async (editSlug: string, password: string) => {
    const response = await api.get(`/manual/owner/${editSlug}`, {
        headers: { 'X-Owner-Password': password }
    });
    return response.data.data;
};

export const ownerSaveManual = async (editSlug: string, password: string, data: any) => {
    const response = await api.put(`/manual/owner/${editSlug}`, data, {
        headers: { 'X-Owner-Password': password }
    });
    return response.data;
};

export const ownerUploadVideo = async (
    editSlug: string,
    password: string,
    file: File | Blob,
    onProgress?: (pct: number) => void
) => {
    const formData = new FormData();
    formData.append('file', file, file instanceof File ? file.name : 'video.mp4');
    const response = await api.post(`/manual/owner/${editSlug}/upload-video`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'X-Owner-Password': password
        },
        onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        }
    });
    return response.data;
};

export const ownerUploadImage = async (editSlug: string, password: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/manual/owner/${editSlug}/upload-image`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'X-Owner-Password': password
        }
    });
    return response.data;
};